import React, { useState, useEffect } from 'react';
import { Offcanvas, Button, Badge } from 'react-bootstrap';
import axios from 'axios';
import { FaDownload, FaTimes } from 'react-icons/fa';

const CreditNotePreview = ({ 
    show, 
    handleClose, 
    creditNote,
    onDownloadPDF 
}) => {
    const [companyDetails, setCompanyDetails] = useState(null);
    const [bankAccounts, setBankAccounts] = useState([]);

    useEffect(() => {
        if (show && creditNote) {
            fetchCompanyDetails();
            fetchBankAccounts();
        }
    }, [show, creditNote]);

    const fetchCompanyDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/settings`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setCompanyDetails(response.data.setting);
        } catch (error) {
            console.error("Error fetching company details:", error);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/bank-accounts?type=bank account`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
                skipLoader: true,
            });

            const firstTwoAccounts = (response.data.account || []).slice(0, 2);
            setBankAccounts(firstTwoAccounts);
        } catch (error) {
            console.error("Error fetching bank accounts:", error);
            setBankAccounts([]);
        }
    };

    const formatAmount = (amount) => {
        return parseFloat(amount || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'approved': { label: 'Approved', variant: 'success' },
            'pending': { label: 'Pending', variant: 'warning' },
            'cancelled': { label: 'Cancelled', variant: 'danger' },
            'draft': { label: 'Draft', variant: 'secondary' }
        };
        
        const config = statusConfig[status] || { label: status, variant: 'secondary' };
        return <Badge bg={config.variant}>{config.label}</Badge>;
    };

    // Number to words function (similar to proforma invoice)
    const numberToWords = (number) => {
        const words = [
            'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
            'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
            'seventeen', 'eighteen', 'nineteen'
        ];

        const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

        if (number < 20) return words[number];
        if (number < 100) {
            return tens[Math.floor(number / 10)] + (number % 10 ? '-' + words[number % 10] : '');
        }
        if (number < 1000) {
            return words[Math.floor(number / 100)] + ' hundred' + (number % 100 ? ' and ' + numberToWords(number % 100) : '');
        }
        return number.toString();
    };

    // Generate amount in words
    const generateAmountInWords = (amount, currency = 'AED') => {
        const integerPart = Math.floor(amount);
        const decimalPart = Math.round((amount - integerPart) * 100);

        let words = numberToWords(integerPart);
        words = words.charAt(0).toUpperCase() + words.slice(1);

        if (decimalPart > 0) {
            words += ' and ' + numberToWords(decimalPart) + ' cents';
        }

        const currencyLabel = currency === 'AED' ? 'UAE' : 'USD';
        return `${currencyLabel} ${words}${decimalPart > 0 ? ` and ${decimalPart}/100` : ''} only.`;
    };

    if (!show || !creditNote) {
        return null;
    }

    // Get client currency from invoice or default to AED
    const clientCurrency = creditNote.invoice?.client?.currency || creditNote.currency || 'AED';
    const vatRate = clientCurrency === 'AED' ? 0.05 : 0;
    
    // Filter bank accounts by currency
    const matchedBanks = bankAccounts.filter(bank =>
        bank.currency?.currency?.toUpperCase() === clientCurrency?.toUpperCase()
    );

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" style={{ width: '890px' }}>
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title className="fw-bold">Credit Note Preview</Offcanvas.Title>
                <div className="position-absolute top-0 end-0 p-3 d-flex gap-2 me-4">
                    <Button
                        variant="light"
                        onClick={() => onDownloadPDF(creditNote.id, creditNote.ref_no)}
                        size="sm"
                        className="shadow-sm"
                        title="Download PDF"
                    >
                        <FaDownload />
                    </Button>
                </div>
            </Offcanvas.Header>

            <Offcanvas.Body className="p-4" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <div className="preview-container" style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
                    {/* Header Section */}
                    <div className="header" style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', marginBottom: '20px' }}>
                        <div className="header-left">
                            <h2 className="company-name" style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '6px' }}>
                                {companyDetails?.company_name || 'Systech Technologies'}
                            </h2>
                            <p className="company-details" style={{ fontSize: '12px', lineHeight: '1.5', margin: 0 }}>
                                {companyDetails?.company_address ? (
                                    <div dangerouslySetInnerHTML={{ __html: companyDetails.company_address }} />
                                ) : (
                                    <>
                                        Dar Al Nahda Building, Office 307<br />
                                        P.O Box 91607
                                    </>
                                )}
                                {companyDetails?.phone && <>{companyDetails.phone}<br /></>}
                                {companyDetails?.email && <>{companyDetails.email}<br /></>}
                            </p>
                        </div>
                        <div className="header-right" style={{ position: 'absolute', right: 0, top: 0, textAlign: 'right' }}>
                            {companyDetails?.logo && (
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}/storage/${companyDetails.logo}`}
                                    style={{ maxWidth: '180px', maxHeight: '80px', marginBottom: '10px' }}
                                    alt="Company Logo"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            )}
                            {/* <div className="document-title" style={{ fontSize: '24px', fontWeight: 'bold', color: 'black', marginTop: '12px' }}>
                                Credit Note
                            </div> */}
                            
                        </div>
                    </div>

                    <div className="divider" style={{ borderTop: '1px solid #ddd', margin: '10px 0 20px 0' }}></div>

                    {/* Credit Note Details */}
                    <div className="details-container" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', width: '100%' }}>
                        <div className="left-column" style={{ float: 'left', width: '48%' }}>
                            <div className="document-info">
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Credit Note No:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {creditNote.ref_no || 'N/A'}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Date:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {new Date(creditNote.date ?? creditNote.created_at).toLocaleDateString('en-GB')}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Original Invoice:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {creditNote.invoice_type === 'tax' ? 'Tax Invoice' : 'Proforma Invoice'}: {creditNote.invoice?.ref_no || ''}
                                    </span>
                                </div>
                                {/* <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Credit Type:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        <Badge bg={creditNote.credit_type === 'full' ? 'success' : 'info'} style={{ fontSize: '11px' }}>
                                            {creditNote.credit_type?.toUpperCase() || 'PARTIAL'}
                                        </Badge>
                                    </span>
                                </div> */}
                            </div>
                        </div>
                        <div className="right-column" style={{ float: 'right', width: '48%' }}>
                            <div className="customer-info">
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Customer:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {creditNote.client?.name || creditNote.client_name || 'N/A'}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Attention:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {creditNote.client_attention || 'Accounts Department'}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Contact Phone:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {creditNote.client_phone || creditNote.client?.contacts?.[0]?.phone || 'N/A'}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Contact Email:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {creditNote.client_email || creditNote.client?.contacts?.[0]?.email || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                  
                    {/* Items Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '11px' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Description</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Original Qty</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Credited Qty</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Unit Price</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Subtotal</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>{vatRate * 100}% VAT</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {creditNote.items && creditNote.items.length > 0 ? (
                                creditNote.items.map((item, index) => {
                                    const creditSubtotal = parseFloat(item.credit_subtotal || 0);
                                    const creditVatAmount = parseFloat(item.credit_vat_amount || 0);
                                    const creditTotal = creditSubtotal + creditVatAmount;

                                    return (
                                        <tr key={index}>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                                {item.description || 'Service/Product'}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                {item.quantity || 0}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: '#dc3545', fontWeight: 'bold' }}>
                                                -{item.credit_quantity || 0}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                {formatAmount(item.unit_price)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: '#dc3545' }}>
                                                -{formatAmount(creditSubtotal)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: '#dc3545' }}>
                                                -{formatAmount(creditVatAmount)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: '#dc3545', fontWeight: 'bold' }}>
                                                -{formatAmount(creditTotal)}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="7" style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontStyle: 'italic' }}>
                                        No credited items available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                <td colSpan="4" style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold', textAlign: 'right' }}>
                                    Total Credit Amount:
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#dc3545' }}>
                                    -{formatAmount(creditNote.basic_amount)}
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#dc3545' }}>
                                    -{formatAmount(creditNote.vat_amount)}
                                </td>
                                <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#dc3545' }}>
                                    -{formatAmount(creditNote.total_amount)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>

                    {/* Amount Summary */}
                    <div style={{ width: '250px', marginLeft: 'auto', fontSize: '11px', marginTop: '10px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: 'none' }}>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', width: '60%', border: 'none' }}>
                                    Subtotal:
                                </td>
                                <td style={{ padding: '4px 0', textAlign: 'right', color: '#dc3545', border: 'none' }}>
                                    -{formatAmount(creditNote.basic_amount)}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>
                                    VAT ({vatRate * 100}%):
                                </td>
                                <td style={{ padding: '4px 0', textAlign: 'right', color: '#dc3545', border: 'none' }}>
                                    -{formatAmount(creditNote.vat_amount)}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>Total Credit:</td>
                                <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 'bold', color: '#dc3545', border: 'none' }}>
                                    -{formatAmount(creditNote.total_amount)}
                                </td>
                            </tr>
                        </table>
                    </div>

                    {/* Amount in Words */}
                    <div style={{
                        marginTop: '20px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '10px 15px',
                        background: '#fafafa',
                        fontSize: '13px',
                        color: '#000'
                    }}>
                        <strong>Credit Amount (in Words):</strong> {generateAmountInWords(creditNote.total_amount, clientCurrency)}
                    </div>

                    {/* Customer Credit Information */}
                    {/* {creditNote.customer_credit && (
                        <div style={{
                            marginTop: '20px',
                            border: '1px solid #28a745',
                            borderRadius: '6px',
                            padding: '12px',
                            background: '#f0f9f0'
                        }}>
                            <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '13px',
                                color: '#28a745',
                                borderBottom: '1px solid #28a745',
                                paddingBottom: '5px'
                            }}>
                                Customer Credit Balance Created
                            </h4>
                            <table width="100%" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                                <tr>
                                    <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Credit ID:</td>
                                    <td style={{ padding: '4px 0' }}>{creditNote.customer_credit.id}</td>
                                </tr>
                                <tr>
                                    <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Available Balance:</td>
                                    <td style={{ padding: '4px 0', color: '#28a745', fontWeight: 'bold' }}>
                                        {formatAmount(creditNote.customer_credit.balance)} {clientCurrency}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Expiry Date:</td>
                                    <td style={{ padding: '4px 0' }}>
                                        {new Date(creditNote.customer_credit.expiry_date).toLocaleDateString('en-GB')}
                                    </td>
                                </tr>
                                {creditNote.customer_credit.notes && (
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Notes:</td>
                                        <td style={{ padding: '4px 0' }}>{creditNote.customer_credit.notes}</td>
                                    </tr>
                                )}
                            </table>
                        </div>
                    )} */}

                    {/* Accounting Information */}
                    {/* <div style={{
                        marginTop: '20px',
                        border: '1px solid #007bff',
                        borderRadius: '6px',
                        padding: '12px',
                        background: '#f0f8ff'
                    }}>
                        <h4 style={{
                            margin: '0 0 10px 0',
                            fontSize: '13px',
                            color: '#007bff',
                            borderBottom: '1px solid #007bff',
                            paddingBottom: '5px'
                        }}>
                            Accounting Information
                        </h4>
                        <table width="100%" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                            <tr>
                                <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Journal Entry:</td>
                                <td style={{ padding: '4px 0' }}>
                                    {creditNote.journal_id ? `Created (ID: ${creditNote.journal_id})` : 'Not Created'}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Created By:</td>
                                <td style={{ padding: '4px 0' }}>
                                    {creditNote.creator?.name || creditNote.created_by || 'System'}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Invoice Status:</td>
                                <td style={{ padding: '4px 0' }}>
                                    {creditNote.invoice?.pi_status === 'fully_credited' ? (
                                        <Badge bg="info" style={{ fontSize: '11px' }}>Fully Credited</Badge>
                                    ) : creditNote.invoice?.pi_status === 'partially_credited' ? (
                                        <Badge bg="warning" style={{ fontSize: '11px' }}>Partially Credited</Badge>
                                    ) : (
                                        <Badge bg="secondary" style={{ fontSize: '11px' }}>Original Status Maintained</Badge>
                                    )}
                                </td>
                            </tr>
                        </table>
                    </div> */}

                    {/* Bank Details */}
                    {matchedBanks.length > 0 && (
                        <div className="bank-details" style={{
                            marginTop: '20px',
                            border: '1px solid #ccc',
                            borderRadius: '6px',
                            padding: '12px',
                            background: '#fff'
                        }}>
                            <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '13px',
                                color: '#000',
                                borderBottom: '1px solid #003366',
                                paddingBottom: '5px'
                            }}>
                                Bank Details
                            </h4>

                            {matchedBanks.length === 1 ? (
                                <table width="100%" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Account Name:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].account_holder_name}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Bank Name:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].name}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Account No:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].account_number}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>IBAN:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].iban}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>SWIFT:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].swift_code}</td>
                                    </tr>
                                </table>
                            ) : (
                                <table width="100%" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <tr>
                                        {matchedBanks.map((bank, index) => (
                                            <td key={index} style={{ verticalAlign: 'top', padding: '0 15px', borderRight: index < matchedBanks.length - 1 ? '1px solid #ccc' : 'none' }}>
                                                <table width="100%" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>Account Name:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.account_holder_name}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>Bank Name:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.name}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>Account No:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.account_number}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>IBAN:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.iban}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>SWIFT:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.swift_code}</td>
                                                    </tr>
                                                </table>
                                            </td>
                                        ))}
                                    </tr>
                                </table>
                            )}
                        </div>
                    )}


                    {/* Remarks */}
                    {creditNote.reason && (
                        <div style={{
                            marginTop: '20px',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '12px',
                            background: '#f8f9fa'
                        }}>
                            <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '13px',
                                color: '#6c757d',
                                borderBottom: '1px solid #6c757d',
                                paddingBottom: '5px'
                            }}>
                                Remarks
                            </h4>
                            <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
                                {creditNote.reason}
                            </div>
                        </div>
                    )}
                </div>
            </Offcanvas.Body>

            <div className="p-4 d-flex justify-content-end border-top bg-light">
                <Button
                    variant="outline-secondary"
                    onClick={handleClose}
                    className="me-2"
                    size="sm"
                >
                    <FaTimes className="me-1" /> Close
                </Button>
                <Button
                    variant="outline-primary"
                    onClick={() => onDownloadPDF(creditNote.id, creditNote.ref_no)}
                    size="sm"
                >
                    <FaDownload className="me-1" /> Download PDF
                </Button>
            </div>

            <style jsx>{`
                @media print {
                    .p-4, .border-top, .bg-light {
                        display: none !important;
                    }
                    
                    .preview-container {
                        margin: 0;
                        padding: 0;
                    }
                    
                    body {
                        width: 190mm;
                        min-height: 200mm;
                        margin: 0 auto;
                        padding: 10mm;
                        font-size: 12px;
                        background-color: #ffffff;
                        border: 1px solid #ddd;
                        border-radius: 12px;
                    }
                }
            `}</style>
        </Offcanvas>
    );
};

export default CreditNotePreview;